import { useState, useMemo } from "react";
import { useGetAllGrowth } from "../../api/scouting";
import { DataTable } from "../../components/ui/data-table";
import { AppButton } from "../../components/ui/AppButton";
import { useNavigate } from "react-router-dom";
import { Icons } from "../../assets/images/icons";
import { PlantGrowth } from "../../types/plantGrowth";
import { Input } from "../../components/ui/input";
import { useTablePrint } from "../../hooks/useTablePrint";
import { ExportButton } from "../../components/ui/ExportButton";

const PlantGrowthPage = () => {
  const navigate = useNavigate();

  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  /** API */
  const { data: plantGrowthData, isLoading } = useGetAllGrowth({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });

  const { handlePrint } = useTablePrint();

  /** Transform API data */
  const transformedPlantGrowthData = useMemo((): PlantGrowth[] => {
    if (!plantGrowthData?.data?.content) return [];

    return plantGrowthData.data.content.map((item: PlantGrowth) => ({
      id: item.id,
      growthStage: item.growthStage,
      date: item.date,
      irrigationStatus: item.irrigationStatus,
      recommendations: item.recommendations,
      weedPresence: item.weedPresence,
    }));
  }, [plantGrowthData]);

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleRowClick = (record: any) => {
    navigate(`/scouting/plant-growth/${record.id}`);
  };

  /** Define columns for the DataTable */
  const plantGrowthColumns = [
    {
      key: "growthStage",
      title: "Growth Stage",
      dataIndex: "growthStage",
      align: "left" as const,
    },
    {
      key: "date",
      title: "Date",
      dataIndex: "date",
      align: "left" as const,
    },
    {
      key: "irrigationStatus",
      title: "Irrigation Status",
      dataIndex: "irrigationStatus",
      align: "left" as const,
    },
    {
      key: "recommendations",
      title: "Recommendations",
      dataIndex: "recommendations",
      align: "left" as const,
      render: (text: string) => (
        <div className="max-w-[220px] truncate" title={text}>
          {text || "-"}
        </div>
      ),
    },
    {
      key: "weedPresence",
      title: "Weed Presence",
      dataIndex: "weedPresence",
      align: "left" as const,
      render: (text: string) => (
        <div className="max-w-[220px] truncate" title={text}>
          {text || "-"}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between">
        {/* Search - Desktop */}
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search here..."
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(
                plantGrowthColumns,
                transformedPlantGrowthData,
                "Plant Growth Report"
              )
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>
          <ExportButton
            columns={plantGrowthColumns}
            data={transformedPlantGrowthData}
            fileName="plant-growth"
            title="Plant Growth Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: plantGrowthData?.data?.totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <DataTable<PlantGrowth>
          data={transformedPlantGrowthData}
          columns={plantGrowthColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: plantGrowthData?.data?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          onRowClick={handleRowClick}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No item found"
          rowKey="id"
        />
      </div>
    </>
  );
};

export default PlantGrowthPage;
